import {
  FC_NAME,
  Script,
  ScriptNode,
} from '@/config/scripts/prudential-annual-checkin';
import { getDynamicGreeting } from '@/utils/greeting';

export interface ScriptState {
  currentNodeId: string;
  currentNode: ScriptNode | null;
  visitedNodes: string[];
  customerResponses: Array<{
    nodeId: string;
    response: string;
    sentiment: 'positive' | 'negative' | 'neutral' | 'question';
    timestamp: Date;
  }>;
  isCompleted: boolean;
  startTime: Date;
  endTime?: Date;
}

export class ScriptEngine {
  private script: Script;
  private state: ScriptState;
  private onStateChange?: (state: ScriptState) => void;

  constructor(script: Script, onStateChange?: (state: ScriptState) => void) {
    this.script = script;
    this.onStateChange = onStateChange;
    this.state = this.initializeState();
  }

  private initializeState(): ScriptState {
    const startNode = this.script.nodes[this.script.startNode];
    return {
      currentNodeId: this.script.startNode,
      currentNode: startNode,
      visitedNodes: [this.script.startNode],
      customerResponses: [],
      isCompleted: false,
      startTime: new Date(),
    };
  }

  getCurrentNode(): ScriptNode | null {
    return this.state.currentNode;
  }

  getScript(): Script {
    return this.script;
  }

  getState(): ScriptState {
    return { ...this.state };
  }

  analyzeCustomerResponse(
    response: string,
  ): 'positive' | 'negative' | 'neutral' | 'question' {
    const lowerResponse = response.toLowerCase().trim();
    const currentNode = this.state.currentNode;

    // Check for off-script questions first
    const questionPatterns = [
      /\b(what|how|why|when|where|who|which)\b.*\?/i,
      /\b(can you|could you|would you|will you)\b.*\?/i,
      /\b(tell me|explain|clarify)\b/i,
      /\b(discussing|discuss|talk about|conversation about)\b/i,
      /\b(got my number|get my number|find my number|contact me)\b/i,
      /\b(what.*about|how.*about)\b/i,
    ];

    if (questionPatterns.some((pattern) => pattern.test(lowerResponse))) {
      return 'question';
    }

    if (!currentNode?.expectedResponse) {
      return 'neutral';
    }

    // Special handling for node 4 (timing question)
    if (currentNode.id === '4') {
      // Check if response is about timing/callback
      const timingPositive =
        /\b(yes|yeah|okay|fine|alright|sure|that's alright|sounds good|works for me|perfect)\b/i;
      const timingNegative =
        /\b(no|not|too soon|different time|another time|later|can't|cant|busy)\b/i;
      const confusedResponse = /\b(what|doing|new stuff|huh|sorry|pardon)\b/i;

      if (confusedResponse.test(lowerResponse)) {
        return 'neutral'; // They didn't understand the question
      }

      if (timingNegative.test(lowerResponse)) {
        return 'negative';
      }

      if (
        timingPositive.test(lowerResponse) &&
        !confusedResponse.test(lowerResponse)
      ) {
        return 'positive';
      }

      return 'neutral'; // Default to neutral for unclear responses
    }

    // Check for positive responses first (exact matches)
    if (
      currentNode.expectedResponse.positive?.some((keyword) =>
        lowerResponse.includes(keyword),
      )
    ) {
      return 'positive';
    }

    // Check for negative responses (exact matches)
    if (
      currentNode.expectedResponse.negative?.some((keyword) =>
        lowerResponse.includes(keyword),
      )
    ) {
      return 'negative';
    }

    // Enhanced sentiment analysis patterns
    const positivePatterns = [
      /^(yes|yeah|yep|yup|sure|okay|ok|alright|fine|good|great|perfect)$/i,
      /\b(yes|yeah|yep|sure|okay|ok|fine|good|great|perfect|interested|sounds good|why not|go ahead|proceed)\b/i,
      /\b(i would|i'd like|please|tell me more|i'm interested|that works)\b/i,
      /\b(can|could|may|might)\s+(you|they|he|she)\s+(call|contact|reach)\b/i,
    ];

    const negativePatterns = [
      /^(no|nope|nah|not really|not now)$/i,
      /\b(no|nope|not|never|don't|dont|can't|cant|cannot|won't|wont|busy|later)\b/i,
      /\b(not interested|no need|no thanks|not necessary|maybe later|another time)\b/i,
      /\b(bad time|wrong time|call back|busy now|in a meeting|driving)\b/i,
      /\b(actually|i'm really|i am really)\s+(busy|occupied|in a meeting|at work)\b/i,
      /\b(moment|time|talk|speak)\b.*\b(don't have|dont have|not have|busy)\b/i,
    ];

    // Check for hearing issues
    const hearingPatterns = [
      /\b(can't hear|cant hear|pardon|what|sorry|repeat|again|hello)\b/i,
      /^(hello|hi|what)$/i,
    ];

    if (hearingPatterns.some((pattern) => pattern.test(lowerResponse))) {
      // For hearing issues, we might want to repeat or go to step 10
      return 'neutral';
    }

    if (positivePatterns.some((pattern) => pattern.test(lowerResponse))) {
      return 'positive';
    }

    if (negativePatterns.some((pattern) => pattern.test(lowerResponse))) {
      return 'negative';
    }

    return 'neutral';
  }

  processCustomerResponse(response: string): ScriptNode | null {
    if (!this.state.currentNode || this.state.isCompleted) {
      return null;
    }

    const sentiment = this.analyzeCustomerResponse(response);

    // Record the response
    this.state.customerResponses.push({
      nodeId: this.state.currentNodeId,
      response,
      sentiment,
      timestamp: new Date(),
    });

    // For questions, don't advance the script
    if (sentiment === 'question') {
      return this.state.currentNode;
    }

    // Determine next node
    const nextNodeId = this.getNextNodeId(sentiment);

    if (!nextNodeId) {
      this.completeScript();
      return null;
    }

    // Move to next node
    this.moveToNode(nextNodeId);
    return this.state.currentNode;
  }

  private getNextNodeId(
    sentiment: 'positive' | 'negative' | 'neutral' | 'question',
  ): string | null {
    const currentNode = this.state.currentNode;
    if (!currentNode?.nextNodes) {
      return null;
    }

    // For questions, stay on the current node
    if (sentiment === 'question') {
      return this.state.currentNodeId;
    }

    // Try to find specific path for sentiment
    const nextNodeId =
      currentNode.nextNodes[sentiment] || currentNode.nextNodes.default;
    return nextNodeId || null;
  }

  moveToNode(nodeId: string): ScriptNode | null {
    const node = this.script.nodes[nodeId];
    if (!node) {
      console.error(`Script node ${nodeId} not found`);
      return null;
    }

    this.state.currentNodeId = nodeId;
    this.state.currentNode = node;
    this.state.visitedNodes.push(nodeId);

    if (node.isEnd) {
      this.completeScript();
    }

    this.notifyStateChange();
    return node;
  }

  private completeScript() {
    this.state.isCompleted = true;
    this.state.endTime = new Date();
    this.notifyStateChange();
  }

  private notifyStateChange() {
    if (this.onStateChange) {
      this.onStateChange(this.getState());
    }
  }

  getProgress(): number {
    const totalNodes = Object.keys(this.script.nodes).length;
    const visitedCount = new Set(this.state.visitedNodes).size;
    return (visitedCount / totalNodes) * 100;
  }

  getScriptForAgent(): string {
    const currentNode = this.state.currentNode;
    if (!currentNode) {
      return '';
    }

    // Replace dynamic greeting placeholder
    let message = currentNode.agentMessage;
    const greeting = getDynamicGreeting();
    message = message.replace(/Good Morning\/Afternoon\/Evening/g, greeting);

    return message;
  }

  requiresCustomerInput(): boolean {
    return this.state.currentNode?.requiresInput || false;
  }

  shouldWaitForResponse(): boolean {
    return this.state.currentNode?.waitForResponse || false;
  }

  generateContextualResponse(question: string): string {
    const lowerQuestion = question.toLowerCase();

    // Responses for common off-script questions
    if (
      /\b(discussing|discuss|talk about|conversation about|what.*about)\b/i.test(
        lowerQuestion,
      )
    ) {
      return `We'll be reviewing your current insurance coverage and financial protection to ensure you're well-prepared for any upcoming milestones in your life. This includes checking if your policies align with your current needs and future goals.`;
    }

    if (
      /\b(got my number|get my number|find my number|contact me|how.*reach)\b/i.test(
        lowerQuestion,
      )
    ) {
      return `Your financial consultant ${FC_NAME} has your contact information from your existing relationship with Prudential. We're reaching out as part of our regular service to ensure your policies continue to meet your needs.`;
    }

    if (/\b(who are you|who is this)\b/i.test(lowerQuestion)) {
      return `I'm Rachel, a virtual assistant calling on behalf of your financial consultant ${FC_NAME} from Prudential. I'm here to help schedule a review of your policies.`;
    }

    if (/\b(why.*calling|purpose.*call|reason.*call)\b/i.test(lowerQuestion)) {
      return `I'm calling to see if you'd like to schedule a review of your insurance policies with ${FC_NAME}. It's been a while since your last review, and we want to ensure your coverage still matches your current needs.`;
    }

    // Default response for other questions
    return `That's a great question. ${FC_NAME} will be able to address all your specific concerns during your consultation. Right now, I'm helping to schedule a time for you both to connect.`;
  }

  isValidWorkingHoursTime(timeString: string): boolean {
    const lowerTime = timeString.toLowerCase();

    // Check for obvious non-working hours
    if (lowerTime.includes('11pm') || lowerTime.includes('11 pm') ||
        lowerTime.includes('10pm') || lowerTime.includes('10 pm') ||
        lowerTime.includes('9pm') || lowerTime.includes('9 pm') ||
        lowerTime.includes('8pm') || lowerTime.includes('8 pm') ||
        lowerTime.includes('7pm') || lowerTime.includes('7 pm') ||
        lowerTime.includes('midnight') || lowerTime.includes('weekend') ||
        lowerTime.includes('saturday') || lowerTime.includes('sunday')) {
      return false;
    }

    // Check for very early morning
    if (lowerTime.includes('6am') || lowerTime.includes('6 am') ||
        lowerTime.includes('7am') || lowerTime.includes('7 am') ||
        lowerTime.includes('5am') || lowerTime.includes('5 am')) {
      return false;
    }

    return true;
  }
}
